import type {ChangeSpec} from '@codemirror/state'
import type {Command, EditorView} from '@codemirror/view'

import {MarkdownLine} from '../../../language-tools/markdown/markdown-line'
import {getNodesOverlappingRange} from '../../../language-tools/syntax-tree/node-range-utils'
import {isPositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getMarkdownTree} from '../../extensions/markdown-parser'
import {createText} from '../../extensions/single-line-editor'
import {getBlockquotePrefixesForLines, hasMixedBlockquoteContent} from './utils'

const BLOCKQUOTE_PREFIX = '> '

/**
 * Handles blockquote formatting by analyzing the selection to determine the appropriate action:
 * - If no blockquotes exist: add blockquote to all lines
 * - If mixed content: make all lines blockquoted
 * - If all blockquotes: remove selected levels
 */
export const formatBlockquote: Command = (view: EditorView) => {
  if (!view.hasFocus) view.focus()

  const tree = getMarkdownTree(view.state)
  const source = view.state.doc.toString()
  const selection = view.state.selection.main
  const selectedLines = MarkdownLine.linesAtRange(tree, source, selection.from, selection.to)
  const blockquotePrefixes = getBlockquotePrefixesForLines(selectedLines)

  if (hasMixedBlockquoteContent(blockquotePrefixes)) {
    // Mixed content - has some non-blockquoted lines

    if (selectedLines.length === 1 && selectedLines[0]?.isBlank) {
      // Handle blank line selection - just add a single blockquote prefix and place cursor after it
      view.dispatch({
        changes: [{from: selection.from, insert: createText(BLOCKQUOTE_PREFIX)}],
        selection: {anchor: selection.from + BLOCKQUOTE_PREFIX.length},
        userEvent: 'input',
        scrollIntoView: true,
      })
      return true
    }

    // Add blockquote prefix to lines that don't already have it
    const changes: ChangeSpec[] = []
    for (const line of selectedLines) {
      const hasBlockquotePrefix = line.prefixes.some(p => p.tokenType === 'blockQuotePrefix')
      if (!hasBlockquotePrefix) {
        changes.push({from: line.start, insert: createText(BLOCKQUOTE_PREFIX)})
      }
    }

    view.dispatch({
      changes,
      userEvent: 'input',
      scrollIntoView: true,
    })
  } else {
    // All lines are blockquoted - remove layers in selection
    const selectedNestingLevels = [...new Set(blockquotePrefixes.map(prefixes => prefixes.length))].sort(
      (a, b) => a - b,
    )

    // Find the outermost blockquote that contains the selection
    const outermostBlockquote = [...getNodesOverlappingRange(tree, selection.from, selection.to)].find(
      node => node.type === 'blockquote',
    )
    if (!outermostBlockquote || !isPositionedNode(outermostBlockquote)) return true

    // Get all lines within the entire blockquote structure
    const blockquoteStart = outermostBlockquote.position.start.offset
    const blockquoteEnd = outermostBlockquote.position.end.offset
    const allBlockquoteLines = MarkdownLine.linesAtRange(tree, source, blockquoteStart, blockquoteEnd)
    const allBlockquotePrefixes = getBlockquotePrefixesForLines(allBlockquoteLines)

    const changes: ChangeSpec[] = []
    // Loop through all the lines in the blockquote and remove the selected nesting levels
    for (const prefixes of allBlockquotePrefixes) {
      let removingLastPrefix = false
      let prefixesRemoved = 0
      for (const level of selectedNestingLevels) {
        if (prefixes.length < level) {
          continue
        }

        const targetNestingLevel = level - 1 // Level 1 = 0 index, Level 2 = 1 index, etc.
        const prefixToRemove = prefixes[targetNestingLevel]
        if (prefixToRemove && isPositionedNode(prefixToRemove)) {
          removingLastPrefix = prefixes.length === level
          prefixesRemoved++
          changes.push({
            from: prefixToRemove.position.start.offset,
            to: prefixToRemove.position.end.offset,
          })
        }
      }

      // If removing the last blockquote prefix but not all prefixes have been removed,
      // insert a space to preserve spacing between the prefix and the text
      const lastPrefix = prefixes[prefixes.length - 1]
      if (lastPrefix && removingLastPrefix && prefixesRemoved < prefixes.length && isPositionedNode(lastPrefix)) {
        changes.push({
          from: lastPrefix.position.start.offset,
          insert: createText(' '),
        })
      }
    }

    view.dispatch({
      changes,
      userEvent: 'input',
      scrollIntoView: true,
    })
  }
  return true
}
